import { Close, UploadOutlined } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { colors } from "../../theme/theme";
import { BASE_URL } from "../../utils/config/config";

const FileUpload = ({
  fileType = "any",
  uploadUrl,
  setUploadUrl,
  setLoading,
}) => {
  const [detectedType, setDetectedType] = useState(null); // "image" or "video"

  const handleBrowseClick = () => {
    document.getElementById("fileInput").click();
  };

  const dataURItoBlob = async (file) => {
    let formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);

      if (file.type.startsWith("image/")) {
        setDetectedType("image");
      } else if (file.type.startsWith("video/")) {
        setDetectedType("video");
      } else {
        setDetectedType(null);
      }

      const response = await axios.post(`${BASE_URL}/aws/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedUrl = response.data.fileUrl;
      setUploadUrl(uploadedUrl);
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateVideoLength = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;
        if (duration <= 30) {
          resolve(true);
        } else {
          reject("Video must be 30 seconds or less");
        }
      };

      video.onerror = () => {
        reject("Invalid video file");
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const getAcceptType = () => {
    if (fileType === "image") return "image/*";
    if (fileType === "video") return "video/*";
    return "image/*,video/*";
  };

  useEffect(() => {
    if (uploadUrl) {
      const ext = uploadUrl.split(".").pop().toLowerCase();
      const videoExts = ["mp4", "webm", "ogg", "mov"];
      const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];

      if (videoExts.includes(ext)) {
        setDetectedType("video");
      } else if (imageExts.includes(ext)) {
        setDetectedType("image");
      } else {
        setDetectedType(null);
      }
    }
  }, [uploadUrl]);

  const renderPreview = () => {
    if (detectedType === "video") {
      return (
        <video
          src={uploadUrl}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          controls
        />
      );
    }

    if (detectedType === "image") {
      return (
        <img
          src={uploadUrl}
          alt="uploaded preview"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      );
    }

    return (
      <Typography color="error" fontSize="14px">
        ⚠️ Unsupported file type preview
      </Typography>
    );
  };

  return (
    <>
      <Box
        sx={{
          border: "2px dashed #000",
          height: "200px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          gap: 1,
          position: "relative",
        }}
      >
        {uploadUrl ? (
          <Box sx={{ overflow: "hidden", width: "100%", height: "100%" }}>
            <IconButton
              onClick={() => {
                setUploadUrl(null);
                setDetectedType(null);
              }}
              sx={{ position: "absolute", top: 0, right: 0, zIndex: 100 }}
            >
              <Close sx={{ color: colors.textColor }} />
            </IconButton>
            {renderPreview()}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
            onClick={handleBrowseClick}
          >
            <Tooltip title="Max file size: 100MB, Max video length: 30s" arrow>
              <UploadOutlined sx={{ fontSize: 40, color: colors.textColor }} />
            </Tooltip>
            <Typography
              sx={{
                color: colors.textColor,
                fontSize: "15px",
                fontFamily: "Inter-SemiBold",
              }}
            >
              Upload media
            </Typography>
          </Box>
        )}
      </Box>

      <input
        type="file"
        id="fileInput"
        style={{ display: "none" }}
        accept={getAcceptType()}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            if (file.type.startsWith("video/")) {
              try {
                validateVideoLength(file);
              } catch (err) {
                alert(err); // or setError(err)
                return;
              }
            }
            dataURItoBlob(file);
          }
        }}
      />
    </>
  );
};

export default FileUpload;
